"use client";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type KeyboardEvent,
} from "react";

import {
  Alert,
  AlertDescription,
  Button,
  Field,
  Markdown,
  Text,
  TextInput,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import { Icon } from "@zapier/zinnia-icons";

import {
  useSplitTreatments,
  useGenerateAiNotesFromFolderAssets,
  useGenerateAiNotesFromFolderAssetsDebugPayload,
} from "@zapier/client";
import { useFolderDocumentationNotes } from "../../../../hooks/useFolderDocumentationNotes";
import { useEmitFolderDocumentationInteractionEvent } from "@zapier/shared";
import { MarkdownFormattingToolbar } from "./MarkdownFormattingToolbar";
import { useFolderNotesEditorKeyDown } from "./useFolderNotesEditorKeyDown";
import { useInsertMarkdownAtSelection } from "./useInsertMarkdownAtSelection";
import { useNotification } from "@zapier/utils";
import {
  DebugAiNotesPanel,
  type DebugAiNotesPanelStatus,
} from "./DebugAiNotesPanel";
import styles from "./FolderNotes.module.css";

interface Props {
  folderId: string;
  hasEligibleFolderAssetIds: boolean;
}

type NotesViewMode = "edit" | "preview";

function RegenerateNotesAlertOverlay({
  title,
  body,
  cancelLabel,
  confirmLabel,
  isDisabled,
  isPending,
  onDismiss,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
  isDisabled: boolean;
  isPending: boolean;
  onDismiss: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className={styles.regenerateAlertOverlay}>
      <Alert onDismiss={onDismiss} status="warning" title={title}>
        <AlertDescription>{body}</AlertDescription>
        <div className={styles.regenerateAlertActions}>
          <Button
            variant="tertiary"
            size="small"
            onPress={onCancel}
            isDisabled={isDisabled}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            size="small"
            onPress={onConfirm}
            isPending={isPending}
            isDisabled={isDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </Alert>
    </div>
  );
}

const NOTES_TEXTAREA_MIN_ROWS = 19;
// Preview text omits markdown syntax (headings, list markers, link syntax, emphasis markers, etc).
// When mapping a click offset from rendered preview text back to raw markdown, this tends to place
// the caret slightly above the user’s intended position. Bias the caret a bit downward.
// This bias is empirically tuned via manual testing across typical notes (different lengths,
// headings/lists/links) to reduce the “caret above click” issue without noticeably overshooting.
const PREVIEW_TO_MARKDOWN_CARET_BIAS = 1.01;

// JSDOM can throw on `window.scrollTo` (“Not implemented”), so guard scroll restoration in tests.
const isJsdomEnvironment = () =>
  globalThis.navigator?.userAgent?.toLowerCase().includes("jsdom") ?? false;

type ScrollContainer = HTMLElement | Window;

const getScrollContainerForElement = (
  element: HTMLElement,
): ScrollContainer => {
  // Prefer preserving scroll on the nearest scrollable ancestor. If none exists,
  // fall back to the window scroll position.
  let current: HTMLElement | null = element.parentElement;
  while (current) {
    const style = globalThis.getComputedStyle(current);
    const isScrollable =
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight;
    if (isScrollable) {
      return current;
    }
    current = current.parentElement;
  }
  return element.ownerDocument.defaultView ?? globalThis.window;
};

interface PinnedToolbarPosition {
  top: number;
  left: number;
  width: number;
}

const getScrollContainerTopOffset = (scrollContainer: ScrollContainer) =>
  scrollContainer instanceof HTMLElement
    ? scrollContainer.getBoundingClientRect().top
    : 0; // Window scrolls relative to the viewport top.

// Pins the markdown toolbar to the top of the scroll container once it would scroll out of view.
// Uses fixed positioning because `position: sticky` can be broken by overflow/clipping ancestors.
function usePinnedMarkdownToolbar({
  isEnabled,
  toolbarSlotReference,
}: {
  isEnabled: boolean;
  toolbarSlotReference: React.RefObject<HTMLDivElement | null>;
}): {
  pinnedPosition: PinnedToolbarPosition | undefined;
  placeholderHeight: number | undefined;
} {
  const isPinnedReference = useRef(false);
  const pinnedPositionReference = useRef<PinnedToolbarPosition>();
  const placeholderHeightReference = useRef<number>();

  const [pinnedPosition, setPinnedPosition] = useState<
    PinnedToolbarPosition | undefined
  >();
  const [placeholderHeight, setPlaceholderHeight] = useState<
    number | undefined
  >();

  useLayoutEffect(() => {
    if (!isEnabled) {
      setPinnedPosition(undefined);
      setPlaceholderHeight(undefined);
      isPinnedReference.current = false;
      pinnedPositionReference.current = undefined;
      placeholderHeightReference.current = undefined;
      return;
    }

    const slotElement = toolbarSlotReference.current;
    if (!slotElement) {
      return;
    }

    const scrollContainer = getScrollContainerForElement(slotElement);

    const updatePinnedState = () => {
      const slot = toolbarSlotReference.current;
      if (!slot) {
        return;
      }

      const containerTop = getScrollContainerTopOffset(scrollContainer);
      const slotRect = slot.getBoundingClientRect();
      const shouldPin = slotRect.top < containerTop;

      const nextPlaceholderHeight =
        slotRect.height > 0 ? slotRect.height : undefined;
      if (placeholderHeightReference.current !== nextPlaceholderHeight) {
        placeholderHeightReference.current = nextPlaceholderHeight;
        setPlaceholderHeight(nextPlaceholderHeight);
      }

      if (!shouldPin) {
        if (isPinnedReference.current) {
          isPinnedReference.current = false;
          pinnedPositionReference.current = undefined;
          setPinnedPosition(undefined);
        }
        return;
      }

      const nextPinnedPosition: PinnedToolbarPosition = {
        top: containerTop,
        left: slotRect.left,
        width: slotRect.width,
      };

      const previousPinnedPosition = pinnedPositionReference.current;
      const isSamePosition =
        previousPinnedPosition &&
        previousPinnedPosition.top === nextPinnedPosition.top &&
        previousPinnedPosition.left === nextPinnedPosition.left &&
        previousPinnedPosition.width === nextPinnedPosition.width;

      isPinnedReference.current = true;
      if (!isSamePosition) {
        pinnedPositionReference.current = nextPinnedPosition;
        setPinnedPosition(nextPinnedPosition);
      }
    };

    updatePinnedState();

    scrollContainer.addEventListener("scroll", updatePinnedState, {
      passive: true,
    });
    globalThis.window?.addEventListener("resize", updatePinnedState);

    return () => {
      scrollContainer.removeEventListener("scroll", updatePinnedState);
      globalThis.window?.removeEventListener("resize", updatePinnedState);
    };
  }, [isEnabled, toolbarSlotReference]);

  return { pinnedPosition, placeholderHeight };
}

export function FolderNotes({ folderId, hasEligibleFolderAssetIds }: Props) {
  const t = useTranslations("folder-documentation");
  const inputId = `folder-notes-${folderId}`;

  const { emit } = useEmitFolderDocumentationInteractionEvent();
  const { isGenerateAiNotesDebugModeEnabled } = useSplitTreatments();
  const { createNotification } = useNotification();

  const { generateAiNotesFromFolderAssets, isGenerating: isGeneratingAiNotes } =
    useGenerateAiNotesFromFolderAssets({ folderId });
  const {
    generateAiNotesFromFolderAssetsDebugPayload,
    isGenerating: isGeneratingAiNotesDebugPayload,
  } = useGenerateAiNotesFromFolderAssetsDebugPayload({ folderId });

  const isGeneratingAnyAiNotes =
    isGeneratingAiNotes || isGeneratingAiNotesDebugPayload;

  const [aiNotesDebug, setAiNotesDebug] = useState<any>();
  const [debugRunStatus, setDebugRunStatus] = useState<
    "idle" | "generating" | "success" | "error"
  >("idle");
  const [debugErrorMessage, setDebugErrorMessage] = useState<
    string | undefined
  >();

  const {
    notesDraft,
    notesSaveStatus,
    isInitialNotesLoading,
    hasHydratedInitialNotes,
    onChangeNotesDraft,
    onBlurNotesDraft,
  } = useFolderDocumentationNotes({ folderId });

  const hasNotes = notesDraft.trim().length > 0;
  const [viewMode, setViewMode] = useState<NotesViewMode>("edit");
  const [isRegenerateNotesAlertOpen, setIsRegenerateNotesAlertOpen] =
    useState(false);
  const toolbarSlotReference = useRef<HTMLDivElement | null>(null);
  const {
    pinnedPosition: toolbarPinnedPosition,
    placeholderHeight: toolbarSlotHeight,
  } = usePinnedMarkdownToolbar({
    isEnabled: viewMode === "edit",
    toolbarSlotReference,
  });
  const hasUserSelectedModeReference = useRef(false);
  const hasInitializedViewModeReference = useRef(false);
  // Clicking controls (Preview/Generate/Regenerate) blurs the textarea. In edit mode, blur normally
  // switches to preview, but for button presses we want to keep the current mode stable.
  const shouldSkipSwitchToPreviewOnBlurReference = useRef(false);
  const shouldFocusInputOnEditReference = useRef(false);
  const pendingCaretFromPreviewReference = useRef<
    | {
        // Character offset within the preview container's rendered text content.
        textIndex: number;
        // Total rendered text length in the preview container.
        textLength: number;
      }
    | undefined
  >(undefined);
  const pendingWindowScrollPositionOnEnterEditReference = useRef<
    | {
        top: number;
        left: number;
      }
    | undefined
  >(undefined);

  // Notes input auto-resizes as the user types (grows/shrinks with content),
  // while still using `NOTES_TEXTAREA_MIN_ROWS` as a minimum to match the current UI baseline.
  const resizeTextareaElement = useCallback((textarea: HTMLTextAreaElement) => {
    const scrollContainer = getScrollContainerForElement(textarea);
    const scrollPositionBefore = {
      top:
        scrollContainer instanceof Window
          ? scrollContainer.scrollY
          : scrollContainer.scrollTop,
      left:
        scrollContainer instanceof Window
          ? scrollContainer.scrollX
          : scrollContainer.scrollLeft,
    };

    // Reset to the default (rows-based) height first so the textarea can also shrink.
    textarea.style.height = "auto";
    textarea.style.overflowY = "hidden";
    const borderBoxHeightAdjustment =
      textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${textarea.scrollHeight + borderBoxHeightAdjustment}px`;

    const scrollTopAfter =
      scrollContainer instanceof Window
        ? scrollContainer.scrollY
        : scrollContainer.scrollTop;
    const didScrollUp = scrollTopAfter < scrollPositionBefore.top;
    if (didScrollUp) {
      if (scrollContainer instanceof Window) {
        if (!isJsdomEnvironment()) {
          scrollContainer.scrollTo(
            scrollPositionBefore.left,
            scrollPositionBefore.top,
          );
        }
      } else {
        scrollContainer.scrollTop = scrollPositionBefore.top;
        scrollContainer.scrollLeft = scrollPositionBefore.left;
      }
    }
  }, []);

  const resizeNotesTextarea = useCallback(() => {
    const inputElement = globalThis.document?.getElementById(inputId);
    if (!(inputElement instanceof HTMLTextAreaElement)) {
      return;
    }
    resizeTextareaElement(inputElement);
  }, [inputId, resizeTextareaElement]);

  // Default behavior:
  // - empty notes: land in Edit (and hide Preview)
  // - existing notes: land in Preview
  useEffect(() => {
    if (!hasHydratedInitialNotes) return;
    if (hasInitializedViewModeReference.current) {
      return;
    }
    if (hasUserSelectedModeReference.current) {
      hasInitializedViewModeReference.current = true;
      return;
    }
    setViewMode(hasNotes ? "preview" : "edit");
    hasInitializedViewModeReference.current = true;
  }, [hasHydratedInitialNotes, hasNotes]);

  useLayoutEffect(() => {
    if (viewMode !== "edit") {
      return;
    }
    resizeNotesTextarea();

    // When switching Preview → Edit, the textarea initially renders at its minimum size.
    // If the user is scrolled near the bottom, the browser can clamp the scroll position
    // upward before we auto-resize the textarea. Restoring the scroll here (after resize)
    // avoids the "jump to top" behavior.
    const scrollPosition =
      pendingWindowScrollPositionOnEnterEditReference.current;
    if (scrollPosition) {
      if (!isJsdomEnvironment()) {
        globalThis.window?.scrollTo(scrollPosition.left, scrollPosition.top);
      }
      pendingWindowScrollPositionOnEnterEditReference.current = undefined;
    }
  }, [notesDraft, resizeNotesTextarea, viewMode]);

  useEffect(() => {
    if (viewMode !== "edit") {
      return;
    }
    if (!shouldFocusInputOnEditReference.current) {
      return;
    }

    const focusInput = () => {
      const inputElement = globalThis.document?.getElementById(inputId);
      if (
        !(inputElement instanceof HTMLTextAreaElement) &&
        !(inputElement instanceof HTMLInputElement)
      ) {
        return;
      }

      const scrollContainer = getScrollContainerForElement(inputElement);
      const scrollPosition = {
        top:
          scrollContainer instanceof Window
            ? scrollContainer.scrollY
            : scrollContainer.scrollTop,
        left:
          scrollContainer instanceof Window
            ? scrollContainer.scrollX
            : scrollContainer.scrollLeft,
      };

      const restoreScrollPosition = () => {
        if (scrollContainer instanceof Window) {
          if (!isJsdomEnvironment()) {
            scrollContainer.scrollTo(scrollPosition.left, scrollPosition.top);
          }
          return;
        }
        scrollContainer.scrollTop = scrollPosition.top;
        scrollContainer.scrollLeft = scrollPosition.left;
      };

      try {
        inputElement.focus({ preventScroll: true });
      } catch {
        // Older browsers may not support focus options.
        inputElement.focus();
      }

      const valueLength = inputElement.value.length;
      const caretFromPreview = pendingCaretFromPreviewReference.current;
      const nextCaretIndex =
        caretFromPreview === undefined
          ? valueLength
          : Math.min(
              Math.round(
                (caretFromPreview.textIndex /
                  Math.max(1, caretFromPreview.textLength)) *
                  valueLength *
                  PREVIEW_TO_MARKDOWN_CARET_BIAS,
              ),
              valueLength,
            );
      inputElement.setSelectionRange(nextCaretIndex, nextCaretIndex);
      pendingCaretFromPreviewReference.current = undefined;
      restoreScrollPosition();

      shouldFocusInputOnEditReference.current = false;
    };

    const scheduleFocus =
      globalThis.requestAnimationFrame ??
      ((callback: FrameRequestCallback) => globalThis.setTimeout(callback, 0));
    scheduleFocus(focusInput);
  }, [inputId, viewMode]);

  const handleEnterEditMode = useCallback(() => {
    if (isGeneratingAnyAiNotes) {
      return;
    }
    hasUserSelectedModeReference.current = true;
    hasInitializedViewModeReference.current = true;
    shouldFocusInputOnEditReference.current = true;
    pendingWindowScrollPositionOnEnterEditReference.current = {
      top: globalThis.window?.scrollY ?? 0,
      left: globalThis.window?.scrollX ?? 0,
    };
    setViewMode("edit");
  }, [isGeneratingAnyAiNotes]);

  const enterPreviewMode = useCallback(() => {
    if (!hasNotes) {
      return;
    }
    hasUserSelectedModeReference.current = true;
    hasInitializedViewModeReference.current = true;
    setViewMode("preview");
  }, [hasNotes]);

  const forcePreviewMode = useCallback(() => {
    // We intentionally bypass the `hasNotes` guard here: after AI generation completes we want to
    // show the result in preview even if the notes were previously empty.
    hasUserSelectedModeReference.current = true;
    hasInitializedViewModeReference.current = true;
    setViewMode("preview");
  }, []);

  const handleSwitchToPreview = useCallback(() => {
    if (!hasNotes) {
      return;
    }
    hasUserSelectedModeReference.current = true;
    hasInitializedViewModeReference.current = true;
    onBlurNotesDraft();
    setViewMode("preview");
  }, [hasNotes, onBlurNotesDraft]);

  const handleBlurAndEnterPreviewMode = useCallback(() => {
    onBlurNotesDraft();
    if (shouldSkipSwitchToPreviewOnBlurReference.current) {
      return;
    }
    enterPreviewMode();
  }, [enterPreviewMode, onBlurNotesDraft]);

  const insertMarkdownAtSelection = useInsertMarkdownAtSelection({
    inputId,
    onChangeValue: onChangeNotesDraft,
  });

  const handleEditorKeyDown = useFolderNotesEditorKeyDown({
    onChangeNotesDraft,
    insertMarkdownAtSelection,
  });

  const statusText =
    notesSaveStatus === "saving"
      ? t("notes.status.saving" as const)
      : notesSaveStatus === "saved"
        ? t("notes.status.saved" as const)
        : notesSaveStatus === "error_retrying"
          ? t("notes.status.failedRetrying" as const)
          : undefined;

  const generateNotesLabel = t("notes.buttons.generate" as const);
  const regenerateNotesLabel = t("notes.buttons.regenerate" as const);

  const regenerateDialogTitle = t("notes.regenerateDialog.title" as const);
  const regenerateDialogBody = t("notes.regenerateDialog.body" as const);
  const regenerateDialogCancel = t("notes.regenerateDialog.cancel" as const);
  const regenerateDialogConfirm = t("notes.regenerateDialog.confirm" as const);

  const generateFailedMessage = t(
    "notes.notifications.generateFailed" as const,
  );

  const isNotesInputDisabled = isInitialNotesLoading || isGeneratingAnyAiNotes;
  const isGenerateNotesDisabled =
    isInitialNotesLoading ||
    isGeneratingAnyAiNotes ||
    !hasEligibleFolderAssetIds;
  const notesInputDisabledText = isGeneratingAnyAiNotes
    ? hasNotes
      ? t("notes.regeneratingNotesPlaceholder" as const)
      : t("notes.generatingNotesPlaceholder" as const)
    : "";
  const shouldShowGenerateNotesDisabledTooltip =
    !isInitialNotesLoading && !hasEligibleFolderAssetIds;
  const generateNotesDisabledTooltipText = t(
    "notes.tooltips.generateDisabled" as const,
  );

  const scheduleFocusNotesInput = useCallback(() => {
    // Closing the regenerate overlay re-renders the textarea; schedule the focus for the next
    // frame so the element exists and focus doesn't get dropped.
    const scheduleFocus =
      globalThis.requestAnimationFrame ??
      ((callback: FrameRequestCallback) => globalThis.setTimeout(callback, 0));
    scheduleFocus(() => {
      const inputElement = globalThis.document?.getElementById(inputId);
      if (!(inputElement instanceof HTMLTextAreaElement)) {
        return;
      }
      inputElement.focus();
    });
  }, [inputId]);

  const handleCloseRegenerateNotesAlert = useCallback(() => {
    setIsRegenerateNotesAlertOpen(false);

    // If the user is currently editing, return focus to the textarea after closing the overlay.
    if (!isNotesInputDisabled && viewMode === "edit") {
      scheduleFocusNotesInput();
    }
  }, [isNotesInputDisabled, scheduleFocusNotesInput, viewMode]);

  const handleGenerateAiNotes = useCallback(async () => {
    if (isGeneratingAnyAiNotes) {
      return;
    }
    if (isGenerateAiNotesDebugModeEnabled) {
      setDebugRunStatus("generating");
      setDebugErrorMessage(undefined);
      setAiNotesDebug(undefined);
    }
    try {
      if (
        isGenerateAiNotesDebugModeEnabled &&
        !isGeneratingAiNotesDebugPayload
      ) {
        try {
          const preflight = await generateAiNotesFromFolderAssetsDebugPayload();
          setAiNotesDebug({ preflight });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error ?? "");
          setAiNotesDebug({
            preflightError: message || "Unknown error",
          });
        }
      }
      const { notes, debug } = await generateAiNotesFromFolderAssets(
        isGenerateAiNotesDebugModeEnabled ? { debug: true } : {},
      );
      onChangeNotesDraft(notes);
      forcePreviewMode();
      if (isGenerateAiNotesDebugModeEnabled) {
        setAiNotesDebug((previous) =>
          previous ? { ...previous, result: debug } : { result: debug },
        );
      } else {
        setAiNotesDebug(debug);
      }
      if (isGenerateAiNotesDebugModeEnabled) {
        setDebugRunStatus("success");
      }
    } catch (error) {
      console.error("Failed to generate AI notes from folder assets:", error);
      createNotification({
        message: generateFailedMessage,
        type: "warning",
      });
      if (isGenerateAiNotesDebugModeEnabled) {
        const message =
          error instanceof Error ? error.message : String(error ?? "");
        setDebugErrorMessage(message || "Unknown error");
        setDebugRunStatus("error");
      }
    }
  }, [
    createNotification,
    generateAiNotesFromFolderAssets,
    generateAiNotesFromFolderAssetsDebugPayload,
    generateFailedMessage,
    forcePreviewMode,
    isGeneratingAnyAiNotes,
    isGeneratingAiNotesDebugPayload,
    isGenerateAiNotesDebugModeEnabled,
    onChangeNotesDraft,
  ]);

  const shouldShowRegenerateNotesAlert = hasNotes && isRegenerateNotesAlertOpen;

  const maybeAlertOverlay = shouldShowRegenerateNotesAlert ? (
    <RegenerateNotesAlertOverlay
      title={regenerateDialogTitle}
      body={regenerateDialogBody}
      cancelLabel={regenerateDialogCancel}
      confirmLabel={regenerateDialogConfirm}
      isDisabled={isInitialNotesLoading}
      isPending={isGeneratingAnyAiNotes}
      onDismiss={handleCloseRegenerateNotesAlert}
      onCancel={handleCloseRegenerateNotesAlert}
      onConfirm={() => {
        setIsRegenerateNotesAlertOpen(false);
        void handleGenerateAiNotes();
      }}
    />
  ) : undefined;

  const renderNotesInput = (inputProps: Record<string, unknown>) => {
    if (isInitialNotesLoading || viewMode === "edit") {
      return (
        <div className={styles.inputArea}>
          {maybeAlertOverlay}
          <div
            className={
              isNotesInputDisabled
                ? `${styles.editorShell} ${styles.editorShellDisabled}`
                : styles.editorShell
            }
          >
            <div
              ref={toolbarSlotReference}
              style={
                toolbarPinnedPosition
                  ? { height: toolbarSlotHeight }
                  : undefined
              }
            >
              <div
                style={toolbarPinnedPosition}
                className={
                  toolbarPinnedPosition
                    ? styles.toolbarWrapperPinned
                    : undefined
                }
              >
                <MarkdownFormattingToolbar
                  isDisabled={isNotesInputDisabled}
                  onInsert={insertMarkdownAtSelection}
                  markdownTooltipText={t("notes.tooltip" as const)}
                />
              </div>
            </div>

            <div className={styles.textInputWrapper}>
              <TextInput
                {...inputProps}
                id={inputId}
                isMultiline={true}
                isFullWidth={true}
                resize="none"
                value={notesDraft}
                placeholder={
                  isInitialNotesLoading
                    ? t("notes.loadingPlaceholder" as const)
                    : t("notes.placeholder" as const)
                }
                isDisabled={isNotesInputDisabled}
                disabledText={notesInputDisabledText}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  resizeTextareaElement(event.target);
                  onChangeNotesDraft(event.target.value ?? "");
                }}
                onBlur={handleBlurAndEnterPreviewMode}
                onKeyDown={handleEditorKeyDown}
                rows={NOTES_TEXTAREA_MIN_ROWS}
                size="large"
              />
            </div>
          </div>
        </div>
      );
    }

    if (viewMode === "preview") {
      const getCaretIndexFromPreviewClick = (
        event: MouseEvent<HTMLDivElement>,
      ):
        | {
            textIndex: number;
            textLength: number;
          }
        | undefined => {
        const container = event.currentTarget;
        const document_ = container.ownerDocument;

        const getCaretRangeFromPoint = () => {
          const anyDocument = document_ as unknown as {
            caretPositionFromPoint?: (
              x: number,
              y: number,
            ) => { offsetNode: Node; offset: number } | null;
            caretRangeFromPoint?: (
              x: number,
              y: number,
            ) => { startContainer: Node; startOffset: number } | null;
          };

          if (anyDocument.caretPositionFromPoint) {
            const position = anyDocument.caretPositionFromPoint(
              event.clientX,
              event.clientY,
            );
            if (!position) return;
            return {
              node: position.offsetNode,
              offset: position.offset,
            };
          }

          if (anyDocument.caretRangeFromPoint) {
            const range = anyDocument.caretRangeFromPoint(
              event.clientX,
              event.clientY,
            );
            if (!range) return;
            return {
              node: range.startContainer,
              offset: range.startOffset,
            };
          }

          return;
        };

        const caret = getCaretRangeFromPoint();
        if (!caret) return undefined;

        const { node, offset } = caret;
        if (!container.contains(node)) return undefined;

        // Convert (node, offset) into a character offset within the container's text content.
        const textLength = container.textContent?.length ?? 0;
        const walker = document_.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
        );
        let current = walker.nextNode();
        let index = 0;
        while (current) {
          if (current === node) {
            return { textIndex: index + offset, textLength };
          }
          index += current.textContent?.length ?? 0;
          current = walker.nextNode();
        }

        return undefined;
      };

      const handlePreviewClick = () => {
        if (isGeneratingAnyAiNotes) {
          return;
        }
        const selection = globalThis.getSelection?.();
        if (selection?.toString()) {
          return;
        }
        handleEnterEditMode();
      };

      const handlePreviewMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        pendingCaretFromPreviewReference.current =
          getCaretIndexFromPreviewClick(event);
      };

      const handlePreviewKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (isGeneratingAnyAiNotes) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleEnterEditMode();
        }
      };

      return (
        <div className={styles.inputArea}>
          {maybeAlertOverlay}
          <hr className={styles.previewDivider} aria-hidden={true} />
          <div
            id={inputId}
            className={
              isGeneratingAnyAiNotes
                ? `${styles.previewBox} ${styles.previewBoxDisabled}`
                : styles.previewBox
            }
            role="button"
            tabIndex={0}
            aria-label={`${t("notes.mode.edit" as const)} ${t("notes.label" as const)}`}
            onClick={handlePreviewClick}
            onMouseDown={handlePreviewMouseDown}
            onKeyDown={handlePreviewKeyDown}
          >
            <div className={styles.previewContent}>
              <Markdown>{notesDraft}</Markdown>
            </div>
          </div>
        </div>
      );
    }

    return;
  };

  return (
    <div className={styles.root}>
      <Field
        label={t("notes.label" as const)}
        inputId={inputId}
        renderLabel={() => (
          <div className={styles.headerRow}>
            <div className={styles.labelRow}>
              <Text tag="p" type="SectionHeader">
                {t("notes.label" as const)}
              </Text>
            </div>

            <div className={styles.statusAndModeControls}>
              {statusText && (
                <span role="status">
                  <Text tag="span" type="Body2" color="TextWeaker">
                    {statusText}
                  </Text>
                </span>
              )}

              {hasNotes ? (
                viewMode === "preview" ? (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="small"
                    iconPosition="start"
                    onPress={handleEnterEditMode}
                    isDisabled={isInitialNotesLoading || isGeneratingAnyAiNotes}
                  >
                    <Icon aria-hidden name="actionEdit" />
                    {t("notes.mode.edit" as const)}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="small"
                    iconPosition="start"
                    onPress={handleSwitchToPreview}
                    onPressStart={() => {
                      shouldSkipSwitchToPreviewOnBlurReference.current = true;
                    }}
                    onPressEnd={() => {
                      shouldSkipSwitchToPreviewOnBlurReference.current = false;
                    }}
                    isDisabled={isInitialNotesLoading || isGeneratingAnyAiNotes}
                  >
                    <Icon aria-hidden name="miscEye" />
                    {t("notes.mode.preview" as const)}
                  </Button>
                )
              ) : undefined}

              {shouldShowGenerateNotesDisabledTooltip ? (
                <Tooltip
                  allowMultiline={true}
                  // The translation key exists in `packages/i18n/messages/en/folders.json`,
                  // but message key types are generated elsewhere; this cast unblocks TS until regenerated.
                  content={generateNotesDisabledTooltipText}
                  placement="bottom"
                >
                  <TooltipFocusableTrigger>
                    <div>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="small"
                        iconPosition="start"
                        aria-disabled="true"
                        isDisabled={true}
                      >
                        <Icon
                          aria-hidden
                          name={hasNotes ? "arrowRefresh" : "actionEditAI"}
                        />
                        {hasNotes ? regenerateNotesLabel : generateNotesLabel}
                      </Button>
                    </div>
                  </TooltipFocusableTrigger>
                </Tooltip>
              ) : (
                <Button
                  type="button"
                  variant="tertiary"
                  size="small"
                  iconPosition="start"
                  isPending={isGeneratingAnyAiNotes}
                  onPressStart={() => {
                    shouldSkipSwitchToPreviewOnBlurReference.current = true;
                  }}
                  onPressEnd={() => {
                    shouldSkipSwitchToPreviewOnBlurReference.current = false;
                  }}
                  onPress={() => {
                    emit({
                      optionSelected: hasNotes
                        ? "regenerate_ai_notes_from_folder_assets"
                        : "generate_ai_notes_from_folder_assets",
                      folderId,
                    });
                    if (hasNotes) {
                      setIsRegenerateNotesAlertOpen(true);
                      return;
                    }
                    void handleGenerateAiNotes();
                  }}
                  isDisabled={isGenerateNotesDisabled}
                >
                  <Icon
                    aria-hidden
                    name={hasNotes ? "arrowRefresh" : "actionEditAI"}
                  />
                  {hasNotes ? regenerateNotesLabel : generateNotesLabel}
                  {isGenerateAiNotesDebugModeEnabled ? (
                    <Icon aria-hidden name="actionBug" />
                  ) : undefined}
                </Button>
              )}
            </div>
          </div>
        )}
        renderInput={renderNotesInput}
      />
      {isGenerateAiNotesDebugModeEnabled && debugRunStatus !== "idle" && (
        <DebugAiNotesPanel
          status={debugRunStatus as DebugAiNotesPanelStatus}
          errorMessage={debugErrorMessage}
          debugPayload={aiNotesDebug}
        />
      )}
    </div>
  );
}
